import { useEffect, useState } from "react";
import { AnnotationMode, getDocument, GlobalWorkerOptions } from "pdfjs-dist";
import { request } from "@/lib/api";
import "./SchoolProposalList.css";

GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

async function renderPdfPages(data: ArrayBuffer) {
  const pdf = await getDocument({ data }).promise;
  const pages: string[] = [];
  try {
    for (let number = 1; number <= pdf.numPages; number += 1) {
      const page = await pdf.getPage(number);
      const sharpness = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale: 1.35 * sharpness });
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) throw new Error("Could not draw the syllabus");
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      await page.render({ canvasContext: context, viewport, annotationMode: AnnotationMode.DISABLE }).promise;
      pages.push(canvas.toDataURL("image/png"));
      page.cleanup();
    }
  } finally {
    await pdf.destroy();
  }
  return pages;
}

function fileName(header: string | null) {
  const match = /filename="([^"]+)"/.exec(header || "");
  return match?.[1] || "Syllabus.pdf";
}

function SyllabusFile({ courseId, mediaId }: { courseId: string; mediaId: string }) {
  const [open, setOpen] = useState(false);
  const [pages, setPages] = useState<string[] | null>(null);
  const [name, setName] = useState("Syllabus.pdf");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || pages) return;
    let cancelled = false;
    setError("");
    (async () => {
      const response = await request(
        `/api/courses/${encodeURIComponent(courseId)}/proposals/syllabus?mediaId=${encodeURIComponent(mediaId)}`,
        { cache: "no-store" },
      );
      if (!response.ok) throw new Error("Could not open the syllabus");
      const bytes = await response.arrayBuffer();
      const drawn = await renderPdfPages(bytes.slice(0));
      if (cancelled) return;
      setName(fileName(response.headers.get("Content-Disposition")));
      setPages(drawn);
    })().catch((err: unknown) => {
      if (cancelled) return;
      setError(err instanceof Error ? err.message : "Could not open the syllabus");
      setPages([]);
    });
    return () => {
      cancelled = true;
    };
  }, [open, pages, courseId, mediaId]);

  return (
    <div className="al-proposal-syllabus">
      <button type="button" className="al-proposal-syllabus__toggle" onClick={() => setOpen((value) => !value)}>
        {open ? "Close PDF" : "Open PDF"}
      </button>
      {open ? (
        <>
          <p className="al-proposal-syllabus__name">{pages ? name : "Syllabus.pdf"}</p>
          {error ? <p className="al-proposal-syllabus__error">{error}</p> : null}
          {pages == null ? <p className="al-proposal-syllabus__wait">Opening the PDF…</p> : null}
          {pages?.map((src, index) => (
            <img key={index} className="al-proposal-syllabus__page" src={src} alt="" />
          ))}
        </>
      ) : null}
    </div>
  );
}

export function ProposalSyllabusDiff({
  courseId,
  currentId,
  proposedId,
}: {
  courseId: string;
  currentId: string | null;
  proposedId: string | null;
}) {
  return (
    <div className="al-proposal-syllabus-diff">
      <div>
        <span className="al-proposal-syllabus-diff__label">Now</span>
        {currentId ? <SyllabusFile courseId={courseId} mediaId={currentId} /> : <p>No syllabus yet</p>}
      </div>
      <div>
        <span className="al-proposal-syllabus-diff__label">Proposed</span>
        {proposedId ? <SyllabusFile courseId={courseId} mediaId={proposedId} /> : <p>Remove the syllabus</p>}
      </div>
    </div>
  );
}
